import { useMemo } from 'react';
import { ResponsiveBar } from "@nivo/bar";
import { useData } from 'context/data.context';
import { CLASS_COLOUR_MAP, CLASSES, CLASS_LABELS } from 'utilities/global.variables';


const formatValue = (value) => {
    const abs = Math.abs(value);
    if (abs >= 1000000) return `£${(abs / 1000000).toFixed(1)}M`;
    if (abs >= 1000) return `£${(abs / 1000).toFixed(0)}k`;
    return `£${abs}`;
};

const CustomLabels = ({ bars }) => {
    return (
        <g>
            {bars.map((bar) => {
                if (bar.height < 20) return null;
                const isLiability = bar.data.indexValue === "Liabilities";
                const label = `${CLASS_LABELS[bar.data.id] || bar.data.id}  ${formatValue(bar.data.value)}`;
                const x = isLiability ? bar.x + bar.width - 8 : bar.x + 8;
                const textAnchor = isLiability ? "end" : "start";

                return (
                    <text
                        key={bar.key}
                        x={x}
                        y={bar.y + 16}
                        textAnchor={textAnchor}
                        fill={["Other", "Savings", "Investment", "Short Term Debt", "Long Term Debt"].includes(bar.data.id) ? "var(--d4)" : "var(--l3)"}
                        fontSize={12}
                        style={{ pointerEvents: "none" }}
                    >
                        {label}
                    </text>
                );
            })}
        </g>
    );
};

const makeBarLabels = (assetTotal, liabilityTotal) => function BarLabels({ bars }) {
    const assetBars = bars.filter(b => b.data.indexValue === "Assets" && b.width > 0);
    const liabilityBars = bars.filter(b => b.data.indexValue === "Liabilities" && b.width > 0);
    const assetLeft = assetBars.length ? Math.min(...assetBars.map(b => b.x)) : 0;
    const assetTop = assetBars.length ? Math.min(...assetBars.map(b => b.y)) : 0;
    const liabilityRight = liabilityBars.length ? Math.max(...liabilityBars.map(b => b.x + b.width)) : 0;
    const liabilityTop = liabilityBars.length ? Math.min(...liabilityBars.map(b => b.y)) : 0;
    return (
        <g>
            {assetBars.length > 0 && (
                <text x={assetLeft + 8} y={assetTop - 8} textAnchor="start" style={{ fill: "var(--l1)", fontSize: 14 }}>
                    {`Assets  ${formatValue(assetTotal)}`}
                </text>
            )}
            {liabilityBars.length > 0 && (
                <text x={liabilityRight - 8} y={liabilityTop - 8} textAnchor="end" style={{ fill: "var(--l1)", fontSize: 14 }}>
                    {`Liabilities  ${formatValue(liabilityTotal)}`}
                </text>
            )}
        </g>
    );
};

const StackedBarsCard = () => {
    const { networthCurrent } = useData();
    const assetsClasses = networthCurrent.assets?.classes ?? [];
    const liabilitiesClasses = networthCurrent.liabilities?.classes ?? [];
    const allClasses = useMemo(() => [...assetsClasses, ...liabilitiesClasses], [assetsClasses, liabilitiesClasses]);

    const assetTotal = assetsClasses.reduce((sum, { valuation }) => sum + Math.abs(valuation), 0);
    const liabilityTotal = liabilitiesClasses.reduce((sum, { valuation }) => sum + Math.abs(valuation), 0);

    const BarLabels = useMemo(() => makeBarLabels(assetTotal, liabilityTotal), [assetTotal, liabilityTotal]);

    const assetsBar = { group: "Assets" };
    assetsClasses.forEach(({ name, valuation }) => {
        if (valuation !== 0) assetsBar[name] = Math.abs(valuation);
    });

    const liabilitiesBar = { group: "Liabilities" };
    liabilitiesClasses.forEach(({ name, valuation }) => {
        if (valuation !== 0) liabilitiesBar[name] = Math.abs(valuation);
    });

    const data = [assetsBar, liabilitiesBar];

    return (
        <div className="r1-1 no-bg">
            <p className="large"></p>
            <div style={{ height: "100%", width: "100%" }}>
                <ResponsiveBar
                    data={data}
                    indexBy="group"
                    keys={CLASSES}
                    colors={({ id }) => CLASS_COLOUR_MAP[id] || "var(--other)"}
                    enableLabel={false}
                    enableGridY={false}
                    borderRadius={3}
                    padding={0.05}
                    innerPadding={0.5}
                    margin={{ top: 30, right: 40, bottom: 20, left: 40 }}
                    axisLeft={null}
                    axisBottom={null}
                    layers={["grid", "axes", "bars", CustomLabels, BarLabels, "legends"]}
                    tooltip={({ id, value, indexValue }) => {
                        const total = indexValue === "Liabilities" ? liabilityTotal : assetTotal;
                        const pct = total > 0 ? Math.round((value / total) * 100) : 0;
                        const items = allClasses.find(c => c.name === id)?.items || [];
                        return (
                            <div className="Tooltip" style={{ background: "var(--d3)", padding: "10px", color: "var(--l3)", borderRadius: "8px", boxShadow: "0 4px 12px rgba(0,0,0,0.3)", width: "max-content" }}>
                                <div className="flex col medium-gap">
                                    <div className="flex row space large-gap">
                                        <p className="regular">{CLASS_LABELS[id] || id}</p>
                                        <p className="number regular">{pct}%</p>
                                    </div>
                                    {items.map(item => (
                                        <p key={item.id} className="regular o60">{item.name}</p>
                                    ))}
                                </div>
                            </div>
                        );
                    }}
                />
            </div>
        </div>
    );
};

export default StackedBarsCard;
